import {createInjectable} from "ngxtension/create-injectable";
import {HttpClient, HttpContext } from "@angular/common/http";
import {signal, computed, inject} from "@angular/core";
import {Site, UploadLogoPayload} from "app/core/interfaces/site.interface";
import { AUTH_DAVESA_API } from "app/core/auth-davesa/auth-davesa-api.component";
import { SitesV2ApiService } from "./sites-v2-api.service";
import { PaginatedResponse } from 'app/core/interfaces/generics/paginated-response.interface';
import { EncryptStorage } from "encrypt-storage";
import { environment } from "environments/environment";
import { Study } from "app/core/interfaces/study.interface";
import { StudiesV2ApiService } from "../../apps/studies/studies-v2-api.service";
import { ParticipantsV2ApiService } from "../../apps/participants/participants-v2-api.service";
import { Participant } from "app/core/interfaces/participant.interface";
import { UserEntities } from "app/core/interfaces/user.interface";
import { EntityDataEnum } from "app/core/enum/entity-data.enum";
import { EntitiesDataV2ApiService } from "../entities-data/entity-data-v2-api.service";

export const encryptStorage = new EncryptStorage(environment.ENCRYPT_STORAGE_KEY, {
  storageType: "sessionStorage",
});

const ALL_SITES = "allSites";
const SITES = "sites";
const SITE = "site";
const STUDIES = "studies";
const SITE_STUDIES = "siteStudies";
const PARTICIPANTS = "participants";
const SITE_PARTICIPANTS = "siteParticipants";
const SITES_PAGINATION = "sitesPagination";

const httpOptions = {
    context: new HttpContext().set(AUTH_DAVESA_API, true),
};

export interface PaginationMetadata {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
}

export const SitesV2Service = createInjectable(() => {
    const _sitesV2ApiService = inject(SitesV2ApiService);
    const _studiesV2ApiService = inject(StudiesV2ApiService);
    const _participantsV2ApiService = inject(ParticipantsV2ApiService);
    const _entityDataV2ApiService = inject(EntitiesDataV2ApiService);

    const allSites = signal<Site[] | null>(null);
    const sites = signal<Site[] | null>(null);
    const site = signal<Site | null>(null);
    const studies = signal<Study[] | null>(null);
    const siteStudies = signal<Study[] | null>(null);
    const participants = signal<Participant[] | null>(null);
    const siteParticipants = signal<Participant[] | null>(null);
    const paginationMetadata = signal<PaginationMetadata | null>(null);

  const allComplianceRoles = signal<any[] | null>(null);
  const allSpecialties = signal<any[] | null>(null);
  const allDelegationRoles = signal<any[] | null>(null);
  const allPrefix = signal<any[] | null>(null);
  const allSuffix = signal<any[] | null>(null);
  const allLocations = signal<any[] | null>(null);
  const allDocumentTypes = signal<any[] | null>(null);
  const allTrainingMethods = signal<any[] | null>(null);
  const allCountryCodes = signal<any[] | null>(null);
  const allSex = signal<any[] | null>(null);
  const allStudyTypes = signal<any[] | null>(null);
  const allStudyPhase = signal<any[] | null>(null);
  const allCurrency = signal<any[] | null>(null);
  const allEsourceFormCategory = signal<any[] | null>(null);
  const allContactCategories = signal<any[] | null>(null);
  const contactCategories = signal<any[] | null>(null);
  const userEntityData = signal<UserEntities[] | null>(null);

    // const params = signal<FilterParams | null>(null);

    const initiate = async (): Promise<boolean> => {
      try {
        return true;
      } catch (error) {
        throw error;
      }
    };
 
    const getAllSites = async (
        offset: number = 0, 
        limit: number = 10, 
        query: string = "",
        sortField: string = "created_at",
        sortDirection: "A" | "D" | "" = "D"
    ): Promise<PaginatedResponse<Site>> => {
      
        try {
          const response = await _sitesV2ApiService.getAllSites(offset, limit, query, sortField, sortDirection);
          console.log("sites", response);

          const rows = response.data.map((data) => {
            return {
                ...data,
                // Add computed properties for display
                full_address: data.full_address,
                phone_number: data.mobile_phone || data.phone_phone || '',
            };
        });
          const sortedRows = transform(rows, "site_name");
          
          // Store current page data
          sites.set(sortedRows);
          
          // Store pagination metadata
          paginationMetadata.set({
            total: response.total,
            page: response.page,
            perPage: response.perPage,
            totalPages: response.totalPages
          });
          
          return {
            ...response,
            data: sortedRows
          };
        }
        catch (error) {
          console.error("Error in getAllSites:", error);
          throw error;
        }
      };

      const getSiteByOid = async (oid: string): Promise<any> => {
        try {
          const response = await _sitesV2ApiService.getSiteByOid(oid);
          site.set(response);
          return response;
        }
        catch (error) {
          console.error("Error in getSiteByOid:", error);
          throw error;
        }
      };  
      
      const createSiteItem = async (data: any): Promise<any> => {
        try {
          const response = await _sitesV2ApiService.createSiteItem(data);
          site.set(response);
          return response;
        }
        catch (error) {
          console.error("Error in createSiteItem:", error);
          throw error;
        }
      };

      const deleteSiteItem = async (oid: string): Promise<any> => {
        try {
          const response = await _sitesV2ApiService.deleteSiteItem(oid);
          site.set(response);
          return response;
        }
        catch (error) {
          console.error("Error in deleteSiteItem:", error);
          throw error;
        }
      };

      const updateSiteItem = async (data: any): Promise<any> => {
        try {
          // TODO: improve API to return updated data, so we don't need to refetch data
          const response = await _sitesV2ApiService.updateSiteItem(data);
          
          const updatedSite = await _sitesV2ApiService.getSiteByOid(data.oid);
          site.set(updatedSite);

          // Update the sites array with the updated site data
          const currentSites = sites();
          const currentAllSites = allSites();
          
          if (currentSites && Array.isArray(currentSites)) {
            const updatedSites = currentSites.map(site => 
              site.oid === updatedSite.oid ? updatedSite : site
            );
            sites.set(updatedSites);
          }
          
          if (currentAllSites && Array.isArray(currentAllSites)) {
            const updatedAllSites = currentAllSites.map(site => 
              site.oid === updatedSite.oid ? updatedSite : site
            );
            allSites.set(updatedAllSites);
          }
          

          return response;
        }
        catch (error) {
          console.error("Error in updateSiteItem:", error);
          throw error;
        }
      };

      const uploadLogo = async (imgData: UploadLogoPayload): Promise<any> => {
        try {
          const response = await _sitesV2ApiService.uploadLogo(imgData);
          return response;
        }
        catch (error) {
          console.error("Error in uploadLogo:", error);
          throw error;
        }
      };

    const transform = (array: any, field: string): any[] => {
        if (!Array.isArray(array)) {
            return;
        }
        array.sort((a: any, b: any) => {
            if (a[field] < b[field]) {
                return -1;
            } else if (a[field] > b[field]) {
                return 1;
            } else {
                return 0;
            }
        });
        return array;
    };
      
    const searchSites = async (
        query: string,
        offset: number = 0, 
        limit: number = 10,
        sortField: string = "created_at",
        sortDirection: "A" | "D" | "" = "D"
    ): Promise<PaginatedResponse<Site>> => {
      try {
        // Use server-side search by calling getAllSites with query parameter
        const response = await getAllSites(offset, limit, query, sortField, sortDirection);
        return response;
      }
      catch (error) {
        console.error("Error in search:", error);
        throw error;
      }
    };
  
    const searchStudies = async (query: string): Promise<any[]> => {
      try {
        const studiesData = studies();
        if (!studiesData || !Array.isArray(studiesData)) {
          return [];
        }
        
        let searchResults = studiesData.filter(
          (searchResults: any) =>
            searchResults.site_name &&
          searchResults.site_name.toLowerCase().includes(query.toLowerCase())
        );
        searchResults.sort((a: any, b: any) => a.site_name.localeCompare(b.site_name));
        studies.set(searchResults);
        return searchResults;
      }
      catch (error) {
        console.error("Error in search:", error);
        throw error;
      }
    };

    const getStudiesBySiteOid = async (query: string): Promise<any[]> => {
      try {
        const studiesData = studies();
        if (!studiesData || !Array.isArray(studiesData)) {
          console.log("No studies data available");
          siteStudies.set([]);
          return [];
        }
        
        let searchResults = studiesData.filter(
          (searchResults: any) =>
            searchResults.site_account?.oid &&
          searchResults.site_account.oid.toLowerCase().includes(query.toLowerCase())
        );
        searchResults.sort((a: any, b: any) => a.number.localeCompare(b.number));
        siteStudies.set(searchResults);
        console.log("query", query);
        console.log("studies", studiesData);
        console.log("siteStudies", siteStudies());

        return searchResults;
      }
      catch (error) {
        console.error("Error in search:", error);
        throw error;
      }
    };

    const getAllStudies = async (): Promise<any[]> => {
      try {
        const response = await _studiesV2ApiService.getAllStudies();
        studies.set(response);
        return response;
      }
      catch (error) {
        console.error("Error in getAllStudies:", error);
        throw error;
      }
    };

    const getAllParticipants = async (): Promise<any[]> => {
      try {
        const response = await _participantsV2ApiService.getAllParticipants();
        participants.set(response);
        return response;
      }
      catch (error) {
        console.error("Error in getAllParticipants:", error);
        throw error;
      }
    };

        const getAllByEntity = async (): Promise<any> => {
            const response = await _entityDataV2ApiService.getAllByEntity();
            userEntityData.set(response);
      
            let getAllComplianceRoles: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.COMPLIANCE_ROLE)
            allComplianceRoles.set(getAllComplianceRoles[0].entityData);
      
            let getAllSpecialties: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SPECIALTY)
            allSpecialties.set(getAllSpecialties[0].entityData);
      
            let getAllPrefix: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.PREFIX)
            allPrefix.set(getAllPrefix[0].entityData);
      
            let getAllSuffix: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SUFFIX)
            allSuffix.set(getAllSuffix[0].entityData);
      
            let getAllLocations: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.LOCATION)
            allLocations.set(getAllLocations[0].entityData);
      
            let getAllDocumentTypes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.DOCUMENT_TYPE)
            allDocumentTypes.set(getAllDocumentTypes[0].entityData);
      
            // let getAllTrainingMethods: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.TRAINING_METHOD)
            // allTrainingMethods.set(getAllTrainingMethods[0].entityData);
      
            let getAllCountryCodes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.COUNTRY_CODE)
            allCountryCodes.set(getAllCountryCodes[0].entityData);
      
            let getAllSex: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.SEX)
            allSex.set(getAllSex[0].entityData);
      
            let getAllStudyTypes: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.STUDY_TYPE)
            allStudyTypes.set(getAllStudyTypes[0].entityData);
      
            let getAllStudyPhase: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.STUDY_PHASE)
            allStudyPhase.set(getAllStudyPhase[0].entityData);
      
            let getAllCurrency: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.CURRENCY)
            allCurrency.set(getAllCurrency[0].entityData);
      
            let getAllEsourceFormCategory: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.ESOURCE_FORM_CATEGORY)
            allEsourceFormCategory.set(getAllEsourceFormCategory[0]?.entityData);
      
            let getAllContactCategory: UserEntities[] = userEntityData().filter(element => element.name === EntityDataEnum.CONTACT_CATEGORY)
            allContactCategories.set(getAllContactCategory[0].entityData);
            contactCategories.set(getAllContactCategory[0].entityData);
            return response;
        };
    
    return {
        sites: computed(() => sites()),
        allSites: computed(() => allSites()),
        site: computed(() => site()),
        studies: computed(() => studies()),
        siteStudies: computed(() => siteStudies()),
        participants: computed(() => participants()),
        siteParticipants: computed(() => siteParticipants()),
        paginationMetadata: computed(() => paginationMetadata()),
        allComplianceRoles: computed(() => allComplianceRoles()),
        allSpecialties: computed(() => allSpecialties()),
        allDelegationRoles: computed(() => allDelegationRoles()),
        allPrefix: computed(() => allPrefix()),
        allSuffix: computed(() => allSuffix()),
        allLocations: computed(() => allLocations()),
        allDocumentTypes: computed(() => allDocumentTypes()),
        allTrainingMethods: computed(() => allTrainingMethods()),
        allCountryCodes: computed(() => allCountryCodes()),
        allSex: computed(() => allSex()),
        allStudyTypes: computed(() => allStudyTypes()),
        allStudyPhase: computed(() => allStudyPhase()),
        allContactCategories: computed(() => allContactCategories()),
        contactCategories: computed(() => contactCategories()),
        userEntityData: computed(() => userEntityData()),
        getAllSites,
        getSiteByOid,
        createSiteItem,
        deleteSiteItem,
        updateSiteItem,
        uploadLogo,
        transform,
        searchSites,
        searchStudies,
        getAllStudies,
        getStudiesBySiteOid,
        initiate,
        getAllParticipants,
        getAllByEntity
    };
});
